<template>
  <div>
    <!-- Loading Spinner -->
    <LoadingComponent v-if="loading" />

    <!-- Cards Section -->
    <div class="grid-container gap-2 mt-4">
      <div class="card-container" v-for="(label, index) in cardLabels" :key="index">
        <div class="inner-card">
          <label>{{ label.name }}</label>
          <span>{{ label.value }}</span>
        </div>
      </div>
    </div>
    <!-- Charts Container with consistent spacing -->
    <div class="mt-6 space-y-4">
      <!-- Visits Chart -->
      <div>
        <div v-if="visitsChartOptions.series.length">
          <highcharts :options="visitsChartOptions" />
        </div>
        <div v-else>
          <p>No Visits chart data available.</p>
        </div>
      </div>

      <!-- Session Length Chart -->
      <div>
        <div v-if="sessionLengthChartOptions.series.length">
          <highcharts :options="sessionLengthChartOptions" />
        </div>
        <div v-else>
          <p>No Session Length chart data available.</p>
        </div>
      </div>

      <!-- Rating Chart -->
      <div>
        <div v-if="ratingChartOptions.series.length">
          <highcharts :options="ratingChartOptions" />
        </div>
        <div v-else>
          <p>No Rating chart data available.</p>
        </div>
      </div>

      <!-- Daily Active Users Chart -->
      <div>
        <div v-if="dailyActiveUsersChartOptions.series.length">
          <highcharts :options="dailyActiveUsersChartOptions" />
        </div>
        <div v-else>
          <p>No Daily Active Users chart data available.</p>
        </div>
      </div>

      <!-- Monthly Active Users Chart -->
      <div>
        <div v-if="monthlyActiveUsersChartOptions.series.length">
          <highcharts :options="monthlyActiveUsersChartOptions" />
        </div>
        <div v-else>
          <p>No Monthly Active Users chart data available.</p>
        </div>
      </div>

      <!-- Age Breakdown Pie Chart -->
      <div>
        <div v-if="ageBreakdownPieChartOptions.series[0].data.length">
          <highcharts :options="ageBreakdownPieChartOptions" />
        </div>
        <div v-else>
          <p>No Age Breakdown data available.</p>
        </div>
      </div>

      <!-- Gender Breakdown Pie Chart -->
      <div>
        <div v-if="genderBreakdownPieChartOptions.series[0].data.length">
          <highcharts :options="genderBreakdownPieChartOptions" />
        </div>
        <div v-else>
          <p>No Gender Breakdown data available.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue';

export default {
  name: "LegoGameStats",
  components: { highcharts: Chart },
  props: {
    games: { type: Array, required: false },
    startDate: { type: String, required: false },
    endDate: { type: String, required: false },
    triggerFetch: { type: Boolean, default: false }
  },
  data() {
    return {
      fetched: false,
      loading: false,
      visitsChartResponses: [],
      sessionLengthChartResponses: [],
      ratingChartResponses: [],
      dailyActiveUsersChartResponses: [],
      monthlyActiveUsersChartResponses: [],
      ageGroupChartResponses: [],
      // Pie chart options for Age Breakdown
      ageBreakdownPieChartOptions: {
        chart: { type: 'pie' },
        title: { text: 'Age Breakdown' },
        tooltip: { pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>' },
        accessibility: { point: { valueSuffix: '%' } },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.percentage:.1f} %' }
          }
        },
        series: [{ name: 'Age Group', colorByPoint: true, data: [] }]
      },
      // Pie chart options for Gender Breakdown
      genderBreakdownPieChartOptions: {
        chart: { type: 'pie' },
        title: { text: 'Gender Breakdown' },
        tooltip: { pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>' },
        accessibility: { point: { valueSuffix: '%' } },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.percentage:.1f} %' }
          }
        },
        series: [{ name: 'Gender', colorByPoint: true, data: [] }]
      },
      // Line chart options for various metrics
      visitsChartOptions: {
        chart: { type: 'line', zoomType: 'x' },
        title: { text: 'Visits Over Time' },
        xAxis: { type: 'datetime' },
        yAxis: { title: { text: 'Visits' } },
        series: []
      },
      sessionLengthChartOptions: {
        chart: { type: 'line', zoomType: 'x' },
        title: { text: 'Session Length Over Time' },
        xAxis: { type: 'datetime' },
        yAxis: { title: { text: 'Session Length' } },
        series: []
      },
      ratingChartOptions: {
        chart: { type: 'line', zoomType: 'x' },
        title: { text: 'Rating Over Time' },
        xAxis: { type: 'datetime' },
        yAxis: { title: { text: 'Rating' } },
        series: []
      },
      dailyActiveUsersChartOptions: {
        chart: { type: 'line', zoomType: 'x' },
        title: { text: 'Daily Active Users Over Time' },
        xAxis: { type: 'datetime' },
        yAxis: { title: { text: 'Daily Active Users' } },
        series: []
      },
      monthlyActiveUsersChartOptions: {
        chart: { type: 'line', zoomType: 'x' },
        title: { text: 'Monthly Active Users Over Time' },
        xAxis: { type: 'datetime' },
        yAxis: { title: { text: 'Monthly Active Users' } },
        series: []
      },
      ageGroupChartOptions: {
        chart: { type: 'line', zoomType: 'x' },
        title: { text: 'Age Group Breakdown Over Time' },
        xAxis: { type: 'datetime' },
        yAxis: { title: { text: 'Engagement' } },
        series: []
      },
      // Store gender breakdown data
      genderBreakdownData: []
    };
  },
  computed: {
    totalVisits() {
      const total = this.visitsChartOptions.series.reduce((total, series) =>
        total + series.data.reduce((sum, point) => sum + Number(point[1]), 0)
      , 0);
      return new Intl.NumberFormat('en-US').format(Math.round(total));
    },
    averageMAU() {
      let sum = 0, count = 0;
      this.monthlyActiveUsersChartOptions.series.forEach(series => {
        if (series.data.length) {
          const avg = series.data.reduce((s, point) => s + Number(point[1]), 0) / series.data.length;
          sum += avg;
          count++;
        }
      });
      return new Intl.NumberFormat('en-US').format(Math.round(count ? sum : 0));
    },
    averageDAU() {
      let sum = 0, count = 0;
      this.dailyActiveUsersChartOptions.series.forEach(series => {
        if (series.data.length) {
          const avg = series.data.reduce((s, point) => s + Number(point[1]), 0) / series.data.length;
          sum += avg;
          count++;
        }
      });
      return new Intl.NumberFormat('en-US').format(Math.round(count ? sum : 0));
    },
    averageSessionLength() {
      let sum = 0, count = 0;
      this.sessionLengthChartOptions.series.forEach(series => {
        if (series.data.length) {
          const avg = series.data.reduce((s, point) => s + Number(point[1]), 0) / series.data.length;
          sum += avg;
          count++;
        }
      });
      return new Intl.NumberFormat('en-US').format(Math.round(count ? sum / count : 0));
    },
    averagePlayerRating() {
      let sum = 0, count = 0;
      this.ratingChartOptions.series.forEach(series => {
        if (series.data.length) {
          const avg = series.data.reduce((s, point) => s + Number(point[1]), 0) / series.data.length;
          sum += avg;
          count++;
        }
      });
      return new Intl.NumberFormat('en-US').format(Math.round(count ? sum / count : 0));
    },
    cardLabels() {
      return [
        { name: 'Total Visits', value: this.totalVisits },
        { name: 'MAU', value: this.averageMAU },
        { name: 'DAU', value: this.averageDAU },
        { name: 'Session Length (mins)', value: this.averageSessionLength },
        { name: 'Player Rating', value: this.averagePlayerRating }
      ];
    }
  },
  created() {
    this.fetchChartsIfNeeded();
  },
  activated() {
    this.fetchChartsIfNeeded();
  },
  watch: {
    triggerFetch() {
        this.fetchChartsIfNeeded();
    },
    startDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetched = false;
      }
    },
    endDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetched = false;
      }
    },
    games(newVal, oldVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        this.fetched = false;
      }
    }
  },
  methods: {
    setLoading(loadingState) {
      this.loading = loadingState;
      this.$emit('loadingState', loadingState);
    },
    async fetchChartsIfNeeded() {
      if (!this.games?.length) return;
      this.setLoading(true);
      await this.fetchCharts();
      this.fetched = true;
      this.setLoading(false);
    },
  async fetchCharts() {
    const { games, startDate, endDate, $http } = this
    if (!games?.length) return          // guard

    const toIso = d => {
      const dt = new Date(d)
      // build UTC-midnight of the given date, then add one day
      const utcPlusOne = new Date(Date.UTC(
        dt.getFullYear(),
        dt.getMonth(),
        dt.getDate() + 1,
      ))
      return utcPlusOne.toISOString()
    }
    const start = toIso(startDate)
    const ends  = toIso(endDate)

    const chartsUrl = '/lego-game-reporting/romonitor/charts'
    const engUrl    = '/lego-game-reporting/romonitor/engagement'

    // each fn(game) returns a promise for one metric
    const metricFetchers = [
      game => $http.get(chartsUrl, {
        params: { name: 'visits', timeslice: 'day', placeId: game.place_id,
                  includeExperienceName: true, start, ends }
      }),
      game => $http.get(chartsUrl, {
        params: { name: 'session-length', timeslice: 'day', placeId: game.place_id,
                  includeExperienceName: true, start, ends }
      }),
      game => $http.get(chartsUrl, {
        params: { name: 'rating', timeslice: 'day', placeId: game.place_id,
                  includeExperienceName: true, start, ends }
      }),
      game => $http.get(engUrl, {
        params: { name: 'engagement', universeId: game.universe_id,
                  dataType: 'DailyActiveUsers', hideTotal: true,
                  timeslice: 'day', start, ends }
      }),
      game => $http.get(engUrl, {
        params: { name: 'engagement', universeId: game.universe_id,
                  dataType: 'MonthlyActiveUsers', hideTotal: true,
                  timeslice: 'day', start, ends }
      }),
      game => $http.get(engUrl, {
        params: { name: 'engagement', universeId: game.universe_id,
                  dataType: 'Visits', breakdown: 'AgeGroup', hideTotal: true,
                  timeslice: 'day', start, ends }
      }),
      game => $http.get(chartsUrl, {
        params: { name: 'ccus', timeslice: 'day', placeId: game.place_id,
                  includeExperienceName: true, start, ends }
      }),
    ]

    // run all 7 fetches *per game*, then drop any game with a failure
    const settled = await Promise.allSettled(
      games.map(game =>
        Promise
          .all(metricFetchers.map(fn => fn(game)))
          .then(responses => ({ game, responses }))
      )
    )

    const goodGames = settled
      .filter(r => r.status === 'fulfilled')
      .map(r => r.value)

    // reset all response arrays
    ;[
      'visitsChartResponses',
      'sessionLengthChartResponses',
      'ratingChartResponses',
      'dailyActiveUsersChartResponses',
      'monthlyActiveUsersChartResponses',
      'ageGroupChartResponses',
      'ccusChartResponses'
    ].forEach(prop => this[prop] = [])

    // unpack each successful game back into your per-chart arrays
    goodGames.forEach(({ game, responses }) => {
      const [visits, sessions, rating, dau, mau, ageGrp, ccus] = responses
      this.visitsChartResponses.push({ game, response: visits })
      this.sessionLengthChartResponses.push({ game, response: sessions })
      this.ratingChartResponses.push({ game, response: rating })
      this.dailyActiveUsersChartResponses.push({ game, response: dau })
      this.monthlyActiveUsersChartResponses.push({ game, response: mau })
      this.ageGroupChartResponses.push({ game, response: ageGrp })
      this.ccusChartResponses.push({ game, response: ccus })
    })

    this.$emit('charts-fetched', {
      visits: this.visitsChartResponses,
      sessionLength: this.sessionLengthChartResponses,
      rating: this.ratingChartResponses,
      dailyActiveUsers: this.dailyActiveUsersChartResponses,
      monthlyActiveUsers: this.monthlyActiveUsersChartResponses,
      ageGroup: this.ageGroupChartResponses,
      ccus: this.ccusChartResponses
    })

    // now render
    this.renderVisitsChart()
    this.renderSessionLengthChart()
    this.renderRatingChart()
    this.renderDailyActiveUsersChart()
    this.renderMonthlyActiveUsersChart()
    this.renderAgeGroupChart()
    this.renderAgeBreakdownPieChart()

    // only pass the universe_ids of games that succeeded
    const universeIds = goodGames.map(g => g.game.universe_id)
    await this.fetchGenderBreakdown(universeIds)
  },

    async fetchGenderBreakdown() {
      try {
        const params = {
          universe_ids: this.games.map(game => game.universe_id),
          start: this.startDate,
          end: this.endDate
        };
        const { data } = await this.$http.get('/portfolio/genders-table-for-universes', { params });
        this.genderBreakdownData = data;
        console.log('Gender Breakdown Data:', this.genderBreakdownData);
        if (this.genderBreakdownData.length) {
          // Assuming the backend returns an aggregated record
          const record = this.genderBreakdownData[0];
          const male = parseFloat(record.Male.replace('%', '')) || 0;
          const female = parseFloat(record.Female.replace('%', '')) || 0;
          this.genderBreakdownPieChartOptions.series[0].data = [
            { name: 'Male', y: male },
            { name: 'Female', y: female }
          ];
        }
      } catch (error) {
        console.error('Error fetching gender breakdown:', error);
      }
    },
    renderVisitsChart() {
      if (!this.visitsChartResponses?.length) return;
      this.visitsChartOptions.series = [];

      this.visitsChartResponses.forEach(({ response }) => {
        const seriesObj = response.data?.[0];
        if (!seriesObj?.data) return;

        const dataPoints = Object
          .entries(seriesObj.data)
          .map(([dateStr, val]) => [new Date(dateStr).getTime(), val]);

        this.visitsChartOptions.series.push({
          name: seriesObj.name,
          data: dataPoints
        });
      });
    },

    renderSessionLengthChart() {
      if (!this.sessionLengthChartResponses?.length) return;
      this.sessionLengthChartOptions.series = [];

      this.sessionLengthChartResponses.forEach(({ response }) => {
        const seriesObj = response.data?.[0];
        if (!seriesObj?.data) return;

        const dataPoints = Object
          .entries(seriesObj.data)
          .map(([dateStr, val]) => [new Date(dateStr).getTime(), val]);

        this.sessionLengthChartOptions.series.push({
          name: seriesObj.name,
          data: dataPoints
        });
      });
    },

    renderRatingChart() {
      if (!this.ratingChartResponses?.length) return;
      this.ratingChartOptions.series = [];

      this.ratingChartResponses.forEach(({ response }) => {
        const seriesObj = response.data?.[0];
        if (!seriesObj?.data) return;

        const dataPoints = Object
          .entries(seriesObj.data)
          .map(([dateStr, val]) => [new Date(dateStr).getTime(), val]);

        this.ratingChartOptions.series.push({
          name: seriesObj.name,
          data: dataPoints
        });
      });
    },

    renderDailyActiveUsersChart() {
      if (!this.dailyActiveUsersChartResponses?.length) return;
      this.dailyActiveUsersChartOptions.series = [];

      this.dailyActiveUsersChartResponses.forEach(({ game, response }) => {
        const seriesObj = response.data?.[0];
        if (!seriesObj?.data) return;

        const dataPoints = Object
          .entries(seriesObj.data)
          .map(([dateStr, val]) => [new Date(dateStr).getTime(), val]);

        this.dailyActiveUsersChartOptions.series.push({
          name: game.name,
          data: dataPoints
        });
      });
    },

    renderMonthlyActiveUsersChart() {
      if (!this.monthlyActiveUsersChartResponses?.length) return;
      this.monthlyActiveUsersChartOptions.series = [];

      this.monthlyActiveUsersChartResponses.forEach(({ game, response }) => {
        const seriesObj = response.data?.[0];
        if (!seriesObj?.data) return;

        const dataPoints = Object
          .entries(seriesObj.data)
          .map(([dateStr, val]) => [new Date(dateStr).getTime(), val]);

        this.monthlyActiveUsersChartOptions.series.push({
          name: game.name,
          data: dataPoints
        });
      });
    },

    renderAgeGroupChart() {
      if (!this.ageGroupChartResponses?.length) return;
      this.ageGroupChartOptions.series = [];

      const aggregated = {};
      this.ageGroupChartResponses.forEach(({ response }) => {
        if (!Array.isArray(response.data)) return;

        response.data.forEach(series => {
          if (series.type === 'metadata' || series.name === 'Total') return;
          aggregated[series.name] ??= {};

          Object.entries(series.data).forEach(([dateStr, val]) => {
            if (val == null) return;
            aggregated[series.name][dateStr] = (aggregated[series.name][dateStr] || 0) + Number(val);
          });
        });
      });

      this.ageGroupChartOptions.series = Object
        .entries(aggregated)
        .map(([groupName, dataMap]) => ({
          name: groupName,
          data: Object
            .entries(dataMap)
            .map(([dateStr, total]) => [new Date(dateStr).getTime(), total])
            .sort((a, b) => a[0] - b[0])
        }));
    },

    renderAgeBreakdownPieChart() {
      if (!this.ageGroupChartResponses?.length) return;

      const totals = {};
      this.ageGroupChartResponses.forEach(({ response }) => {
        if (!Array.isArray(response.data)) return;

        response.data.forEach(series => {
          if (series.type === 'metadata' || series.name === 'Total') return;
          totals[series.name] = (totals[series.name] || 0)
            + Object.values(series.data).reduce((sum, v) => sum + (v == null ? 0 : Number(v)), 0);
        });
      });

      this.ageBreakdownPieChartOptions.series[0].data = Object
        .entries(totals)
        .map(([name, y]) => ({ name, y }));
    },
  }
};
</script>

<style lang="postcss" scoped>
.inner-card {
  @apply flex flex-col gap-2 items-center;
}
.card-container span {
  @apply text-3xl font-bold;
}
.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0;
}
.card-container {
  @apply bg-white rounded-md flex flex-col items-center justify-center p-4;
}
</style>
